import React, { useState, useEffect, useRef, useMemo } from "react"
import { Link } from "gatsby"
import SectionHeading from "components/section-heading"
import CroppedImage from "components/cropped-image"

import { getViewport } from "utils"
import LeftArrowIcon from "../../../images/left-arrow-carousel.svg"
import RightArrowIcon from "../../../images/right-arrow-carousel.svg"
import {
  AnimatedSliderSection,
  Container,
  Carousel,
  SlideWrapper,
  Item,
  MediaContainer,
  VideoElement,
  ImageElement,
  CroppedImageElement,
  CategoryTag,
  Headline,
  AuthorByline,
  NavigationArrow,
  PaginationContainer,
  PaginationDot,
  HeadingRow,
  ContentBelowCarousel,
  ArrowsWrapper,
} from "./style"

const transformDataForSlider = (items) =>
  items.map((item, index) => {
    const baseData = {
      id: item.id || `item-${index}`,
      title: null,
      description: null,
      author: null,
      category: null,
      image: null,
      imageCrops: null,
      videoUrl: null,
      posterImage: null,
      articleUrl: null,
    }
    switch (item.internal?.type) {
      case "ContentfulCard": {
        const videoUrl = item.previewVideo ? extractVideoUrl(item.previewVideo) : null

        // Get imageCrops data or fallback to extracted image
        const imageCropsData = getImageCropsData(item)
        const fallbackImage = imageCropsData ? null : extractCardImage(item)

        // Extract article data
        const { article } = item
        const title =
          article?.titles?.site || article?.titles?.body || item.headlineCard || item.headline
        const description =
          article?.previewText ||
          item.subheadline?.childMarkdownRemark?.html?.replace(/<[^>]*>/g, "") ||
          null
        const category = article?.category || item.category || null
        const author = article?.authors?.[0]?.name || item.byline || null
        const articleUrl = article?.url ? `/articles/${article.url}` : null

        return {
          ...baseData,
          title,
          description,
          author,
          category,
          image: fallbackImage,
          imageCrops: imageCropsData,
          videoUrl,
          posterImage: fallbackImage,
          articleUrl,
        }
      }
      case "ContentfulPerson":
        return {
          ...baseData,
          title: item.name,
          description: item.shortBio?.raw
            ? extractTextFromRichText(item.shortBio.raw)
            : item.personTitle,
          author: item.credentials || item.personTitle,
          category: "Expert",
          image: item.image?.[0]?.secure_url || null,
        }
      case "ContentfulProductHighlight":
        return {
          ...baseData,
          title: item.heading?.childMarkdownRemark?.html?.replace(/^<p>(.*)<\/p>$/i, "$1") || null,
          description: item.description?.childMarkdownRemark?.html?.replace(/<[^>]*>/g, "") || null,
          author: item.subLabel || null,
          category:
            item.preHeadingText?.childMarkdownRemark?.html?.replace(/<[^>]*>/g, "") || "Product",
          image: item.image?.[0]?.secure_url || null,
        }
      case "ContentfulFeatureHighlight":
        return {
          ...baseData,
          title: item.label || "Feature Highlight",
          description: item.value || null,
          image: item.image?.[0]?.secure_url || null,
          category: "Feature",
        }
      case "ContentfulTestimonial":
        return {
          ...baseData,
          title:
            item.headline?.childMarkdownRemark?.html?.replace(/^<p>(.*)<\/p>$/i, "$1") ||
            "Testimonial",
          description: item.bodyText?.childMarkdownRemark?.html?.replace(/<[^>]*>/g, "") || null,
          author: item.attribution?.name || item.attributionImage?.name || "Customer",
          category: "Testimonial",
          image:
            item.attribution?.image?.[0]?.secure_url ||
            item.attributionImage?.[0]?.secure_url ||
            null,
        }
      case "ContentfulTextWithMedia":
        return {
          ...baseData,
          title: item.heading?.childMarkdownRemark?.html?.replace(/^<p>(.*)<\/p>$/i, "$1") || null,
          description: item.bodyText?.childMarkdownRemark?.html?.replace(/<[^>]*>/g, "") || null,
          author: null,
          category:
            item.preHeadingText?.childMarkdownRemark?.html?.replace(/<[^>]*>/g, "") || "Content",
          image: extractMediaImage(item.media),
          videoUrl: extractMediaVideo(item.media),
        }
      default:
        return {
          ...baseData,
          title: item.headline || item.name || `Item ${index + 1}`,
          description: "Content item",
          category: "General",
        }
    }
  })

const getImageCropsData = (item) => {
  // 1. Try item.imageCrops first
  if (item.imageCrops) {
    return item.imageCrops
  }

  // 2. Try article.previewImage if it's parsed JSON with crop data
  if (item.article?.previewImage) {
    try {
      const previewData = JSON.parse(item.article.previewImage)
      // Only return if it has proper crop structure
      if (previewData.crops || previewData.path) {
        return previewData
      }
    } catch (e) {
      // Handle JSON parsing errors - previewImage may contain invalid JSON or non-JSON strings
      console.warn("Failed to parse article.previewImage as JSON:", e.message)
    }
  }

  return null
}

const extractVideoUrl = (video) => {
  if (!video) return null

  let videoUrl = null

  if (video.videoObject && video.videoObject.length > 0) {
    videoUrl = video.videoObject[0].secure_url || video.videoObject[0].url || null
  } else {
    videoUrl = video.url || video.videoUrl || video.secure_url || null
  }

  return videoUrl
}

const extractTextFromRichText = (rawRichText) => {
  try {
    const parsed = JSON.parse(rawRichText)
    if (parsed.content && parsed.content[0]?.content) {
      const text = parsed.content[0].content
        .filter((item) => item.nodeType === "text")
        .map((item) => item.value)
        .join(" ")
        .substring(0, 150)
      return `${text}...`
    }
  } catch (e) {
    // Fallback for non-JSON rich text
  }
  return null
}

const extractMediaImage = (media) => {
  if (!media) return null
  if (media.internal?.type === "ContentfulImage") {
    return media.url || media.secure_url || media.image?.[0]?.secure_url || null
  }
  return null
}

const extractMediaVideo = (media) => {
  if (!media) return null
  if (media.internal?.type === "ContentfulVideo") {
    return media.url || media.videoUrl || null
  }
  return null
}

const extractCardImage = (item) => {
  let imageUrl = null

  // 1. Try item.image first
  if (item.image) {
    if (typeof item.image === "string" && item.image.startsWith("https://")) {
      imageUrl = item.image
    } else if (typeof item.image === "string" && item.image.startsWith("img/")) {
      imageUrl = `https://mindbodygreen-res.cloudinary.com/image/upload/${item.image.replace(
        "img/",
        "",
      )}`
    }
  }

  // 2. Try imageCrops if no image found
  if (!imageUrl && item.imageCrops) {
    if (item.imageCrops.crops && item.imageCrops.crops.length > 0) {
      const firstCrop = item.imageCrops.crops[0]
      if (firstCrop.path && firstCrop.path.startsWith("https://")) {
        imageUrl = firstCrop.path
      }
    }
    if (!imageUrl && item.imageCrops.path) {
      const { path } = item.imageCrops
      if (path.startsWith("https://")) {
        imageUrl = path
      } else if (path.startsWith("img/")) {
        imageUrl = `https://mindbodygreen-res.cloudinary.com/image/upload/${path.replace(
          "img/",
          "",
        )}`
      }
    }
  }

  // 3. Try article.previewImage if no image found
  if (!imageUrl && item.article?.previewImage) {
    try {
      const previewData = JSON.parse(item.article.previewImage)

      // Try crops first
      if (previewData.crops && previewData.crops.length > 0) {
        imageUrl = previewData.crops[0].path
      }
      // Fallback to path
      if (!imageUrl && previewData.path) {
        if (previewData.path.startsWith("https://")) {
          imageUrl = previewData.path
        } else if (previewData.path.startsWith("img/")) {
          const cleanPath = previewData.path.replace("img/", "")
          imageUrl = `https://mindbodygreen-res.cloudinary.com/image/upload/${cleanPath}`
        }
        // Debug log
      }
    } catch (e) {
      // Handle error silently
    }
  }

  return imageUrl
}

// Helper to compute the current active slide index from rotation
const getCurrentIndex = (currentDegree, degreePerItem, totalItems) => {
  if (!totalItems || !degreePerItem) return 0
  const rawIndex = Math.round(-currentDegree / degreePerItem)
  // Proper modulo for negatives
  return ((rawIndex % totalItems) + totalItems) % totalItems
}

const SliderContentOverlay = ({ item, isActive, isTransitioning }) => {
  if (!item) return null

  const content = (
    <ContentBelowCarousel
      className="card-header"
      isActive={isActive}
      isTransitioning={isTransitioning}
    >
      {item.category && <CategoryTag isActive={isActive}>{item.category}</CategoryTag>}

      {item.title && <Headline isActive={isActive}>{item.title || `Slide ${item.id}`}</Headline>}

      {item.author && <AuthorByline isActive={isActive}>By {item.author}</AuthorByline>}
    </ContentBelowCarousel>
  )

  if (item.articleUrl) {
    return (
      <Link to={item.articleUrl} style={{ textDecoration: "none", width: "100%" }}>
        {content}
      </Link>
    )
  }

  return content
}

const AnimatedSlider = ({
  gridData,
  autoPlay = true,
  autoPlayInterval = 6000,
  hideOnPageLoad,
  dataId,
  id,
  elRef,
  title,
  subtitle,
  isFocusedItem,
  ...props
}) => {
  const originalItems = gridData?.items

  const items = useMemo(() => transformDataForSlider(originalItems || []), [originalItems])

  if (!originalItems?.length) {
    return null
  }

  const [currentDegree, setCurrentDegree] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const videoRefs = useRef([])
  const intervalRef = useRef(null)

  // Touch/swipe state for mobile
  const [touchStart, setTouchStart] = useState(null)
  const [touchEnd, setTouchEnd] = useState(null)
  const [touchStartY, setTouchStartY] = useState(null)
  const [touchEndY, setTouchEndY] = useState(null)
  const [isMobile] = useState(() => getViewport() === "mobile")

  const totalItems = items.length
  const degreePerItem = totalItems > 0 ? 360 / totalItems : 0

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50

  useEffect(() => {
    if (autoPlay && totalItems > 1) {
      restartAutoPlay()
    } else {
      pauseAutoPlay()
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
    // We intentionally avoid adding handleNext to dependencies to prevent re-creating the interval
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPlay, autoPlayInterval, totalItems])

  useEffect(() => {
    const currentIndex = getCurrentIndex(currentDegree, degreePerItem, totalItems)
    videoRefs.current.forEach((video, index) => {
      if (video) {
        if (index === currentIndex) {
          video.play().catch(() => {})
        } else {
          video.pause()
          // eslint-disable-next-line no-param-reassign
          video.currentTime = 0
        }
      }
    })
  }, [currentDegree, degreePerItem, totalItems])

  const handleNext = () => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setCurrentDegree((prev) => prev - degreePerItem)
    setTimeout(() => setIsTransitioning(false), 1000)
  }

  const handlePrev = () => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setCurrentDegree((prev) => prev + degreePerItem)
    setTimeout(() => setIsTransitioning(false), 1000)
  }

  const handleDotClick = (index) => {
    if (isTransitioning) return
    pauseAutoPlay()
    setIsTransitioning(true)
    const currentIndex = getCurrentIndex(currentDegree, degreePerItem, totalItems)
    const diff = index - currentIndex
    const shortestPath =
      Math.abs(diff) > totalItems / 2 ? (diff > 0 ? diff - totalItems : diff + totalItems) : diff
    setCurrentDegree((prev) => prev - shortestPath * degreePerItem)
    setTimeout(() => setIsTransitioning(false), 1000)
    restartAutoPlay()
  }

  // Touch event handlers for mobile swipe
  const onTouchStart = (e) => {
    if (!isMobile) return
    setTouchEnd(null)
    setTouchEndY(null)
    setTouchStart(e.targetTouches[0].clientX)
    setTouchStartY(e.targetTouches[0].clientY)
  }

  const onTouchMove = (e) => {
    if (!isMobile) return
    setTouchEnd(e.targetTouches[0].clientX)
    setTouchEndY(e.targetTouches[0].clientY)
  }

  const onTouchEnd = () => {
    if (!isMobile || touchStart === null || touchEnd === null) return

    const deltaX = touchStart - touchEnd
    const deltaY = (touchStartY ?? 0) - (touchEndY ?? 0)
    const absX = Math.abs(deltaX)
    const absY = Math.abs(deltaY)

    // Only treat as swipe if horizontal intent is stronger than vertical and exceeds threshold
    if (absX > absY && absX > minSwipeDistance) {
      if (deltaX > 0) {
        handleNextManual()
      } else {
        handlePrevManual()
      }
    }
  }

  const pauseAutoPlay = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }

  const resumeAutoPlay = () => {
    if (!autoPlay || totalItems <= 1) return
    if (intervalRef.current) return
    intervalRef.current = setInterval(() => {
      handleNext()
    }, autoPlayInterval)
  }

  const restartAutoPlay = () => {
    if (!autoPlay || totalItems <= 1) return
    pauseAutoPlay()
    resumeAutoPlay()
  }

  const handleNextManual = () => {
    pauseAutoPlay()
    handleNext()
    resumeAutoPlay()
  }

  const handlePrevManual = () => {
    pauseAutoPlay()
    handlePrev()
    resumeAutoPlay()
  }

  if (!items || items.length === 0) {
    return null
  }

  return (
    <>
      <HeadingRow hasContent={title || subtitle} isFocusedItem={isFocusedItem}>
        <SectionHeading title={title} subtitle={subtitle} isFocusedItem={isFocusedItem} />
      </HeadingRow>
      <AnimatedSliderSection
        hideOnPageLoad={hideOnPageLoad}
        data-exp-id={dataId}
        id={id}
        ref={elRef}
        {...props}
      >
        <div
          onMouseEnter={pauseAutoPlay}
          onMouseLeave={resumeAutoPlay}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <Container>
            {totalItems > 1 && (
              <ArrowsWrapper>
                <NavigationArrow
                  position="left"
                  onClick={handlePrevManual}
                  disabled={isTransitioning}
                  style={{ pointerEvents: "auto" }}
                  aria-label="Previous slide"
                  type="button"
                  onFocus={pauseAutoPlay}
                  onBlur={resumeAutoPlay}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowRight") {
                      e.preventDefault()
                      handleNextManual()
                    } else if (e.key === "ArrowLeft") {
                      e.preventDefault()
                      handlePrevManual()
                    }
                  }}
                >
                  <img src={LeftArrowIcon} alt="" aria-hidden="true" width="28" height="29" />
                </NavigationArrow>
                <NavigationArrow
                  position="right"
                  onClick={handleNextManual}
                  disabled={isTransitioning}
                  style={{ pointerEvents: "auto" }}
                  aria-label="Next slide"
                  type="button"
                  onFocus={pauseAutoPlay}
                  onBlur={resumeAutoPlay}
                  onKeyDown={(e) => {
                    if (e.key === "ArrowRight") {
                      e.preventDefault()
                      handleNextManual()
                    } else if (e.key === "ArrowLeft") {
                      e.preventDefault()
                      handlePrevManual()
                    }
                  }}
                >
                  <img src={RightArrowIcon} alt="" aria-hidden="true" width="24" height="24" />
                </NavigationArrow>
              </ArrowsWrapper>
            )}

            <Carousel rotation={currentDegree}>
              {items.map((item, index) => {
                const angle = index * degreePerItem
                const currentIndex = getCurrentIndex(currentDegree, degreePerItem, totalItems)
                const isActive = index === currentIndex

                return (
                  <SlideWrapper key={item.id || index} angle={angle}>
                    <Item rotation={currentDegree}>
                      <MediaContainer isActive={isActive} isTransitioning={isTransitioning}>
                        {item.videoUrl ? (
                          <VideoElement
                            ref={(el) => (videoRefs.current[index] = el)}
                            src={item.videoUrl}
                            muted
                            loop
                            playsInline
                            poster={item.image}
                          />
                        ) : item.imageCrops ? (
                          <CroppedImageElement>
                            <CroppedImage
                              imageData={item.imageCrops}
                              alt=""
                              sizes={[
                                {
                                  query: 992,
                                  width: 747,
                                  height: 418,
                                  transforms: "c_fill,g_center,w_747,h_418,q_90,f_auto,fl_lossy",
                                },
                                {
                                  query: 768,
                                  width: 640,
                                  height: 358,
                                  transforms: "c_fill,g_center,w_640,h_358,q_90,f_auto,fl_lossy",
                                },
                                {
                                  query: 576,
                                  width: 520,
                                  height: 291,
                                  transforms: "c_fill,g_center,w_520,h_291,q_90,f_auto,fl_lossy",
                                },
                                {
                                  query: 0,
                                  width: 400,
                                  height: 224,
                                  transforms: "c_fill,g_center,w_400,h_224,q_90,f_auto,fl_lossy",
                                },
                              ]}
                            />
                          </CroppedImageElement>
                        ) : (
                          <ImageElement
                            baseUrl={item.image}
                            images={[
                              {
                                transforms: "c_fill,g_center,w_900,h_550,q_90,f_auto,fl_lossy",
                                screenSize: 1200,
                              },
                              {
                                transforms: "c_fill,g_center,w_780,h_430,q_90,f_auto,fl_lossy",
                                screenSize: 960,
                              },
                              {
                                transforms: "c_fill,g_center,w_660,h_430,q_90,f_auto,fl_lossy",
                                screenSize: 576,
                              },
                              {
                                transforms: "c_fill,g_center,w_480,h_320,q_90,f_auto,fl_lossy",
                              },
                            ]}
                            alt=""
                          />
                        )}
                      </MediaContainer>

                      {isActive && totalItems > 1 && (
                        <PaginationContainer>
                          {items.map((dotItem, dotIndex) => (
                            <PaginationDot
                              key={`dot-${dotItem.id || dotIndex}`}
                              isActive={dotIndex === currentIndex}
                              aria-label={`Go to slide ${dotIndex + 1} of ${totalItems}`}
                              aria-current={dotIndex === currentIndex ? "true" : undefined}
                              type="button"
                              onClick={() => handleDotClick(dotIndex)}
                              onFocus={pauseAutoPlay}
                              onBlur={resumeAutoPlay}
                              onKeyDown={(e) => {
                                if (e.key === "ArrowRight") {
                                  e.preventDefault()
                                  handleNextManual()
                                } else if (e.key === "ArrowLeft") {
                                  e.preventDefault()
                                  handlePrevManual()
                                }
                              }}
                            />
                          ))}
                        </PaginationContainer>
                      )}
                    </Item>
                  </SlideWrapper>
                )
              })}
            </Carousel>
          </Container>

          {/* Content below the carousel */}
          <SliderContentOverlay
            item={items[getCurrentIndex(currentDegree, degreePerItem, totalItems)]}
            isActive
            isTransitioning={isTransitioning}
          />
        </div>
      </AnimatedSliderSection>
    </>
  )
}

export default AnimatedSlider
